import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UseInlineLayoutParams {
  navItems: { id: string; label: string }[];
  /** URL search param key for active tab (e.g. "tab"). When set, active nav syncs with URL. */
  searchParamKey?: string | undefined;
  /** Default active nav id when no valid param. Defaults to first nav item. */
  defaultActiveId?: string | undefined;
}

export interface UseInlineLayoutReturn {
  activeId: string;
  handleNavClick: (id: string) => void;
}

export function useInlineLayout({
  navItems,
  searchParamKey,
  defaultActiveId,
}: UseInlineLayoutParams): UseInlineLayoutReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramValue = searchParamKey ? searchParams.get(searchParamKey) : null;

  const validIds = useMemo(() => navItems.map((item) => item.id), [navItems]);
  const resolvedDefault = defaultActiveId ?? navItems[0]?.id ?? '';
  const initialActive =
    paramValue && validIds.includes(paramValue) ? paramValue : resolvedDefault;

  const [activeId, setActiveId] = useState(initialActive);

  useEffect(() => {
    if (!searchParamKey) {
      return;
    }
    if (paramValue && validIds.includes(paramValue)) {
      setActiveId(paramValue);
    } else {
      setActiveId(resolvedDefault);
      setSearchParams({ [searchParamKey]: resolvedDefault }, { replace: true });
    }
  }, [searchParamKey, paramValue, validIds, resolvedDefault, setSearchParams]);

  const handleNavClick = (id: string) => {
    setActiveId(id);
    if (searchParamKey) {
      setSearchParams({ [searchParamKey]: id }, { replace: true });
    }
  };

  return { activeId, handleNavClick };
}
